import React, { useState, useEffect } from 'react';
import api from '../../api';

const STATUS = {
  scheduled:         { bg: '#ede9fe', text: '#6d28d9', label: 'Scheduled' },
  in_progress:       { bg: '#fef3c7', text: '#b45309', label: 'In Progress' },
  completed:         { bg: '#dcfce7', text: '#166534', label: 'Completed' },
  issue_found:       { bg: '#fee2e2', text: '#991b1b', label: 'Issue Found' },
};

function ChecklistItem({ item, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
      cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}>
      <input type="checkbox" checked={item.done} onChange={e => onChange({ ...item, done: e.target.checked })}
        style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#0d9488', flexShrink: 0 }} />
      <span style={{ fontSize: 13, color: item.done ? '#94a3b8' : '#1e293b',
        textDecoration: item.done ? 'line-through' : 'none' }}>{item.task}</span>
    </label>
  );
}

function TurnoverCard({ turnover, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [checklist, setChecklist] = useState(turnover.checklist || []);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(turnover.status);

  const done = checklist.filter(i => i.done).length;
  const total = checklist.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const s = STATUS[status] || STATUS.scheduled;

  async function saveChecklist(newList) {
    setChecklist(newList);
    setSaving(true);
    try { await api.put(`/pm/turnovers/${turnover.id}`, { checklist: newList }); }
    catch {} finally { setSaving(false); }
  }

  async function updateStatus(newStatus) {
    setStatus(newStatus);
    setSaving(true);
    try { await api.put(`/pm/turnovers/${turnover.id}`, { status: newStatus }); onUpdate(); }
    catch {} finally { setSaving(false); }
  }

  return (
    <div className="card mb-3">
      <div style={{ padding: '14px 18px', cursor: 'pointer' }} onClick={() => setExpanded(e => !e)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>
              {turnover.property_name} · {turnover.unit_number}
              {turnover.unit_name && <span style={{ fontWeight: 400, color: '#64748b' }}> — {turnover.unit_name}</span>}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
              {turnover.checkout_date
                ? <>Checkout: {new Date(turnover.checkout_date + 'T12:00').toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' })}</>
                : <span style={{ color: '#ef4444' }}>No checkout date</span>}
              {turnover.checkin_date && <> · Check-in: {new Date(turnover.checkin_date + 'T12:00').toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' })}</>}
              {turnover.assigned_to && <> · {turnover.assigned_to}</>}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            {total > 0 && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>{done}/{total} tasks</div>
                <div style={{ width: 64, height: 5, background: '#e2e8f0', borderRadius: 3 }}>
                  <div style={{ height: '100%', width: `${pct}%`,
                    background: pct === 100 ? '#16a34a' : '#0d9488', borderRadius: 3, transition: 'width .3s' }} />
                </div>
              </div>
            )}
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 5,
              background: s.bg, color: s.text }}>{s.label}</span>
            <span style={{ color: '#94a3b8', fontSize: 12 }}>{expanded ? '▲' : '▼'}</span>
          </div>
        </div>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid #e2e8f0', padding: '14px 18px' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#64748b', marginRight: 2 }}>Status:</span>
            {Object.entries(STATUS).map(([key, val]) => (
              <button key={key} onClick={() => updateStatus(key)} disabled={saving}
                style={{ fontSize: 11, fontWeight: 700, padding: '4px 11px', borderRadius: 5, border: 'none',
                  cursor: 'pointer', transition: 'all .15s',
                  background: key === status ? val.bg : '#f1f5f9',
                  color: key === status ? val.text : '#64748b' }}>
                {val.label}
              </button>
            ))}
            {saving && <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}>Saving…</span>}
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase',
            letterSpacing: '.06em', marginBottom: 4 }}>
            Checklist — {done}/{total} complete
          </div>
          {checklist.map((item, i) => (
            <ChecklistItem key={i} item={item} onChange={updated => {
              const newList = checklist.map((it, idx) => idx === i ? updated : it);
              saveChecklist(newList);
            }} />
          ))}
          {turnover.notes && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#64748b', fontStyle: 'italic',
              padding: '8px 12px', background: '#f8fafc', borderRadius: 6 }}>
              {turnover.notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const INPUT = { width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 7,
  border: '1px solid #e2e8f0', background: '#f8fafc', color: '#1e293b', boxSizing: 'border-box' };

function AddTurnoverModal({ units, onSave, onClose }) {
  const [form, setForm] = useState({ unit_id: '', checkout_date: '', checkin_date: '', type: 'clean', assigned_to: '', notes: '' });
  const [saving, setSaving] = useState(false);
  async function handleSave() {
    if (!form.unit_id) return;
    setSaving(true);
    try { await api.post('/pm/turnovers', form); onSave(); }
    catch {} finally { setSaving(false); }
  }
  const set = key => e => setForm(p => ({ ...p, [key]: e.target.value }));
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 420, padding: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16, color: '#1e293b' }}>Schedule Turnover</div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Unit *</label>
          <select value={form.unit_id} onChange={set('unit_id')} style={INPUT}>
            <option value="">— select unit —</option>
            {units.map(u => <option key={u.id} value={u.id}>{u.property_name} · {u.unit_number}{u.name ? ` — ${u.name}` : ''}</option>)}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          {[['checkout_date', 'Checkout Date'], ['checkin_date', 'Check-in Date']].map(([k, l]) => (
            <div key={k}>
              <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>{l}</label>
              <input type="date" value={form[k]} onChange={set(k)} style={INPUT} />
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Type</label>
            <select value={form.type} onChange={set('type')} style={INPUT}>
              <option value="clean">Clean</option>
              <option value="inspection">Inspection</option>
              <option value="maintenance_window">Maintenance Window</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Assigned To</label>
            <input value={form.assigned_to} onChange={set('assigned_to')} placeholder="Cleaner or staff" style={INPUT} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !form.unit_id}>
            {saving ? 'Saving…' : 'Schedule'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function TurnoversView() {
  const [turnovers, setTurnovers] = useState([]);
  const [units, setUnits] = useState([]);
  const [filter, setFilter] = useState('scheduled');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  function load() {
    const q = filter ? `?status=${filter}` : '';
    Promise.all([api.get(`/pm/turnovers${q}`), api.get('/pm/units')])
      .then(([t, u]) => { setTurnovers(t.data); setUnits(u.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, [filter]);

  return (
    <>
      <div className="page-header">
        <span className="page-title">Turnovers</span>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>+ Schedule Turnover</button>
      </div>
      <div className="page-body">
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[['scheduled', 'Scheduled'], ['in_progress', 'In Progress'], ['completed', 'Completed'], ['', 'All']].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)}
              className={`btn btn-sm ${filter === v ? 'btn-primary' : 'btn-ghost'}`}>{l}</button>
          ))}
        </div>

        {loading
          ? <div className="loading">Loading…</div>
          : turnovers.length === 0
            ? <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                No turnovers{filter ? ` with status "${filter.replace('_', ' ')}"` : ''}.
              </div></div>
            : turnovers.map(t => <TurnoverCard key={t.id} turnover={t} onUpdate={load} />)
        }
      </div>
      {showAdd && <AddTurnoverModal units={units} onSave={() => { setShowAdd(false); load(); }} onClose={() => setShowAdd(false)} />}
    </>
  );
}
